import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
  EmptyState,
  LoadingState,
  TypeaheadAvatar,
  TypeaheadDropdownContainer,
  TypeaheadDropdownHeader,
  TypeaheadInfo,
  TypeaheadOption,
  TypeaheadPrimaryText,
  TypeaheadScrollArea,
  TypeaheadSecondaryText,
} from 'features/richText/components/TypeaheadDropdownStyles'
import { useUserSearch } from 'features/richText/hooks/useUserSearch'
import { createMentionNode } from 'features/richText/nodes/MentionNode'
import { $createTextNode, TextNode } from 'lexical'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { TaggableUser } from 'types'

const TRIGGER_CHAR = '@'

class MentionTypeaheadOption extends MenuOption {
  user: TaggableUser
  picture: string

  constructor(user: TaggableUser) {
    super(user.name || user.email)
    this.user = user
    this.picture = '' // We don't have user pictures in the current data
  }
}

interface MentionPluginProps {
  /**
   * Maximum number of mention suggestions to show
   */
  maxSuggestions?: number
}

export const MentionPlugin: React.FC<MentionPluginProps> = ({
  maxSuggestions = 10,
}) => {
  const [queryString, setQueryString] = useState<string | null>(null)
  const { searchUsers, isLoading } = useUserSearch({
    maxResults: maxSuggestions,
  })

  const options = useMemo(() => {
    if (!queryString) return []
    return searchUsers(queryString).map(
      (user) => new MentionTypeaheadOption(user)
    )
  }, [queryString, searchUsers])

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      const mentionNode = createMentionNode(selectedOption.user)
      if (nodeToReplace) {
        nodeToReplace.replace(mentionNode)
      }
      mentionNode.insertAfter($createTextNode(' '))
      closeMenu()
    },
    []
  )

  const checkForMentionMatch = useBasicTypeaheadTriggerMatch(TRIGGER_CHAR, {
    minLength: 0,
    allowWhitespace: true,
  })

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ) => {
        if (!anchorElementRef.current) return null

        return createPortal(
          <TypeaheadDropdownContainer $minWidth='16rem' $maxWidth='20rem'>
            <TypeaheadDropdownHeader>Mention User</TypeaheadDropdownHeader>
            <TypeaheadScrollArea>
              {isLoading ? (
                <LoadingState>Loading users...</LoadingState>
              ) : options.length === 0 ? (
                <EmptyState>No users found</EmptyState>
              ) : (
                options.map((option, index) => (
                  <TypeaheadOption
                    key={option.user.id}
                    ref={option.setRefElement}
                    $isSelected={selectedIndex === index}
                    onClick={() => {
                      setHighlightedIndex(index)
                      selectOptionAndCleanUp(option)
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}>
                    <TypeaheadAvatar $shape='circle'>
                      {(option.user.name ||
                        option.user.email)[0]?.toUpperCase()}
                    </TypeaheadAvatar>
                    <TypeaheadInfo>
                      <TypeaheadPrimaryText>
                        {option.user.name || option.user.email}
                      </TypeaheadPrimaryText>
                      {option.user.email && option.user.name && (
                        <TypeaheadSecondaryText>
                          {option.user.email}
                        </TypeaheadSecondaryText>
                      )}
                    </TypeaheadInfo>
                  </TypeaheadOption>
                ))
              )}
            </TypeaheadScrollArea>
          </TypeaheadDropdownContainer>,
          anchorElementRef.current
        )
      }}
    />
  )
}
